export function parsePageRanges(expression: string, pageCount: number): number[] {
  return parsePageRangeGroups(expression, pageCount).flat();
}

export type SplitMode = "ranges" | "every" | "selected";

export function buildSplitPageGroups(
  mode: SplitMode,
  expression: string,
  pagesPerOutput: number,
  pageCount: number
): number[][] {
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    throw new Error("The document has no pages.");
  }
  if (mode === "every") {
    if (!Number.isInteger(pagesPerOutput) || pagesPerOutput < 1) {
      throw new Error("Pages per output must be a positive integer.");
    }
    const pages = Array.from({ length: pageCount }, (_, index) => index + 1);
    const groups: number[][] = [];
    for (let index = 0; index < pages.length; index += pagesPerOutput) {
      groups.push(pages.slice(index, index + pagesPerOutput));
    }
    return groups;
  }

  const groups = parsePageRangeGroups(expression, pageCount);
  return mode === "selected" ? [groups.flat()] : groups;
}

export function splitOutputName(group: readonly number[], index: number): string {
  return group.length === 1
    ? `page-${String(group[0]).padStart(3, "0")}.pdf`
    : `part-${String(index + 1).padStart(3, "0")}.pdf`;
}

function parsePageRangeGroups(expression: string, pageCount: number): number[][] {
  const trimmed = expression.trim();
  if (!trimmed || !Number.isInteger(pageCount) || pageCount < 1) {
    throw new Error("Page range is empty or the document has no pages.");
  }

  const groups: number[][] = [];
  const seen = new Set<number>();
  for (const rawPart of trimmed.split(",")) {
    const part = rawPart.trim();
    const single = /^(\d+)$/.exec(part);
    const range = /^(\d+)-(\d+)$/.exec(part);
    let values: number[];
    if (single) {
      values = [Number(single[1])];
    } else if (range) {
      const start = Number(range[1]);
      const end = Number(range[2]);
      if (start > end) throw new Error(`Invalid descending page range: ${part}`);
      values = Array.from({ length: end - start + 1 }, (_, index) => start + index);
    } else {
      throw new Error(`Invalid page range: ${part || "empty"}`);
    }

    for (const page of values) {
      if (page < 1 || page > pageCount) throw new Error(`Page ${page} is outside 1-${pageCount}.`);
      if (seen.has(page)) throw new Error(`Page ${page} is selected more than once.`);
      seen.add(page);
    }
    groups.push(values);
  }
  return groups;
}
